/**
 * Aggressive Lazy Loading System
 * Loads images only when they enter the viewport with optimized thresholds
 * Supports both native lazy loading and custom IntersectionObserver
 */

(function() {
  'use strict';

  // Configuration
  const config = {
    rootMargin: '50px 0px', // Load images 50px before they enter viewport (mobile-optimized)
    threshold: 0.01, // Trigger when 1% visible
    enableNativeLazyLoad: true,
    placeholderClass: 'lazy-placeholder',
    loadedClass: 'lazy-loaded',
    errorClass: 'lazy-error',
    fadeInDuration: 200
  };

  // Check if IntersectionObserver is supported
  const supportsIntersectionObserver = 'IntersectionObserver' in window;
  const supportsNativeLazyLoad = 'loading' in HTMLImageElement.prototype;

  // Create placeholder SVG based on aspect ratio
  function createPlaceholder(width, height) {
    const aspectRatio = height / width;
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}'%3E%3Crect width='${width}' height='${height}' fill='%23f0f0f0'/%3E%3C/svg%3E`;
  }

  // Decode and load image
  function loadImage(img) {
    return new Promise((resolve, reject) => {
      const src = img.dataset.src || img.dataset.lazySrc;
      const srcset = img.dataset.srcset || img.dataset.lazySrcset;

      if (!src && !srcset) {
        reject(new Error('No src or srcset found'));
        return;
      }

      // Use a temporary image to preload
      const tempImg = new Image();

      tempImg.onload = function() {
        // Set the actual image attributes
        if (srcset) {
          img.srcset = srcset;
        }
        if (src) {
          img.src = src;
        }

        // Decode if supported for smoother rendering
        if (img.decode) {
          img.decode()
            .then(() => {
              img.classList.add(config.loadedClass);
              img.classList.remove(config.placeholderClass);
              resolve(img);
            })
            .catch(() => {
              img.classList.add(config.loadedClass);
              img.classList.remove(config.placeholderClass);
              resolve(img);
            });
        } else {
          img.classList.add(config.loadedClass);
          img.classList.remove(config.placeholderClass);
          resolve(img);
        }
      };

      tempImg.onerror = function() {
        img.classList.add(config.errorClass);
        reject(new Error('Failed to load image'));
      };

      // Start loading
      if (srcset) {
        tempImg.srcset = srcset;
      }
      if (src) {
        tempImg.src = src;
      }
    });
  }

  // Load background image
  function loadBackgroundImage(element) {
    return new Promise((resolve, reject) => {
      const bgUrl = element.dataset.bgSrc || element.dataset.lazyBg;

      if (!bgUrl) {
        reject(new Error('No background image URL found'));
        return;
      }

      const tempImg = new Image();
      tempImg.onload = function() {
        element.style.backgroundImage = `url('${bgUrl}')`;
        element.classList.add(config.loadedClass);
        element.classList.remove(config.placeholderClass);
        resolve(element);
      };

      tempImg.onerror = function() {
        element.classList.add(config.errorClass);
        reject(new Error('Failed to load background image'));
      };

      tempImg.src = bgUrl;
    });
  }

  // IntersectionObserver callback
  function handleIntersection(entries, observer) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;

        // Unobserve immediately to prevent duplicate loads
        observer.unobserve(element);

        // Load based on element type
        if (element.tagName === 'IMG') {
          loadImage(element).catch(err => {
            console.warn('Lazy load image error:', err);
          });
        } else if (element.dataset.bgSrc || element.dataset.lazyBg) {
          loadBackgroundImage(element).catch(err => {
            console.warn('Lazy load background error:', err);
          });
        }
      }
    });
  }

  // Initialize lazy loading
  function init() {
    // Add CSS for fade-in effect
    if (!document.getElementById('lazy-load-styles')) {
      const style = document.createElement('style');
      style.id = 'lazy-load-styles';
      style.textContent = `
        .lazy-placeholder {
          opacity: 0;
          transition: opacity ${config.fadeInDuration}ms ease-in;
        }
        .lazy-loaded {
          opacity: 1;
        }
        .lazy-error {
          opacity: 0.5;
          background: #f0f0f0;
        }
        img[data-src]:not([src]),
        img[data-lazy-src]:not([src]) {
          background: #f0f0f0;
        }
      `;
      document.head.appendChild(style);
    }

    // Get all lazy-loadable elements
    const lazyImages = document.querySelectorAll('img[data-src], img[data-lazy-src], img[data-srcset], img[data-lazy-srcset]');
    const lazyBackgrounds = document.querySelectorAll('[data-bg-src], [data-lazy-bg]');

    // If native lazy loading is supported and enabled, use it
    if (config.enableNativeLazyLoad && supportsNativeLazyLoad) {
      lazyImages.forEach(img => {
        // Use native loading for images with data-src
        if (!img.loading) {
          img.loading = 'lazy';
        }

        // Still need to swap data-src to src
        const src = img.dataset.src || img.dataset.lazySrc;
        const srcset = img.dataset.srcset || img.dataset.lazySrcset;

        if (src && !img.src) {
          img.src = src;
        }
        if (srcset && !img.srcset) {
          img.srcset = srcset;
        }

        img.classList.add(config.loadedClass);
      });

      // Still use IntersectionObserver for background images
      if (supportsIntersectionObserver && lazyBackgrounds.length > 0) {
        const bgObserver = new IntersectionObserver(handleIntersection, {
          rootMargin: config.rootMargin,
          threshold: config.threshold
        });

        lazyBackgrounds.forEach(bg => {
          bg.classList.add(config.placeholderClass);
          bgObserver.observe(bg);
        });
      }

      return;
    }

    // Fallback: Use IntersectionObserver for all elements
    if (supportsIntersectionObserver) {
      const observer = new IntersectionObserver(handleIntersection, {
        rootMargin: config.rootMargin,
        threshold: config.threshold
      });

      lazyImages.forEach(img => {
        img.classList.add(config.placeholderClass);
        observer.observe(img);
      });

      lazyBackgrounds.forEach(bg => {
        bg.classList.add(config.placeholderClass);
        observer.observe(bg);
      });
    } else {
      // Fallback: Load all images immediately (old browsers)
      lazyImages.forEach(img => {
        loadImage(img).catch(err => console.warn('Lazy load error:', err));
      });

      lazyBackgrounds.forEach(bg => {
        loadBackgroundImage(bg).catch(err => console.warn('Lazy load error:', err));
      });
    }
  }

  // Expose public API
  window.AggressiveLazyLoad = {
    init: init,
    loadImage: loadImage,
    loadBackgroundImage: loadBackgroundImage,
    config: config
  };

  // Auto-initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on dynamic content (for AJAX-loaded sections)
  const reinitObserver = new MutationObserver((mutations) => {
    let shouldReinit = false;

    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1) { // Element node
          if (node.matches('img[data-src], img[data-lazy-src], [data-bg-src], [data-lazy-bg]') ||
              node.querySelector('img[data-src], img[data-lazy-src], [data-bg-src], [data-lazy-bg]')) {
            shouldReinit = true;
          }
        }
      });
    });

    if (shouldReinit) {
      init();
    }
  });

  // Observe the entire document for dynamic content
  if (document.body) {
    reinitObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      reinitObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    });
  }

})();
